import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Terminal, RefreshCw } from 'lucide-react';
import Tabs, { type Tab } from '../components/common/Tabs';
import StatusBadge from '../components/common/StatusBadge';
import SSHAccess from '../components/edges/SSHAccess';
import KeyRotationManagement from '../components/edges/KeyRotationManagement';
import mtgeService from '../services/mtge.service';
import type { MtgeDevice } from '../services/mtge.service';

const statusMap: Record<string, string> = {
  pending_approval: 'Pending',
  approved: 'Approved',
  provisioned: 'Provisioned',
  active: 'Online',
  offline: 'Offline',
};

const MTGEConfig = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mtge, setMtge] = useState<MtgeDevice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMtge = async () => {
      if (!id) {
        setError('MTGE ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await mtgeService.getById(id);
        setMtge(data);
        setError(null);
      } catch (err: any) {
        console.error('Failed to load MTGE:', err);
        setError(err.message || 'Failed to load shared gateway details');
      } finally {
        setLoading(false);
      }
    };

    fetchMtge();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading gateway configuration...</div>
      </div>
    );
  }

  if (error || !mtge) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600">{error || 'Shared gateway not found'}</div>
      </div>
    );
  }

  const tabs: Tab[] = [
    { id: 'ssh-access', label: 'SSH Access', icon: Terminal, content: <SSHAccess edgeGuid={mtge.serial_number} edgeStatus={mtge.status === 'provisioned' || mtge.status === 'active' ? 'provisioned' : mtge.status} /> },
    { id: 'key-rotation', label: 'Key Rotation', icon: RefreshCw, content: <KeyRotationManagement edgeGuid={mtge.serial_number} /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/mtges/${id}`)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Gateway Configuration</h1>
            <div className="flex items-center space-x-3 mt-2">
              <p className="text-gray-600">{mtge.device_name} &bull; {mtge.serial_number}</p>
              <StatusBadge status={statusMap[mtge.status] || mtge.status} />
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Tabs */}
      <div className="card">
        <Tabs tabs={tabs} />
      </div>
    </div>
  );
};

export default MTGEConfig;
